import React from "react"
import { motion } from "framer-motion"
import {
  Diamond,
  Trophy,
  Star,
  Medal,
  Building,
  Users,
  Sparkles,
  Briefcase,
  HeartHandshake,
} from "lucide-react"
import { PhotoPlaceholder } from "../photo-placeholder"

/* -----------------------------------------------------------------------------
   Data
----------------------------------------------------------------------------- */

const tiers = [
  {
    title: "Diamond",
    price: "₹5,00,000",
    icon: Diamond,
    color: "from-pink-500/20 via-pink-400/10 to-transparent",
    bullets: [
      "Prime branding across materials",
      "Prime large exhibition booth",
      "Keynote session",
      "05 complimentary registrations",
    ],
  },
  {
    title: "Platinum",
    price: "₹3,00,000",
    icon: Trophy,
    color: "from-indigo-500/20 via-indigo-400/10 to-transparent",
    bullets: [
      "Prominent branding",
      "Medium exhibition booth",
      "03 complimentary registrations",
      "Keynote talk",
    ],
  },
  {
    title: "Gold",
    price: "₹2,00,000",
    icon: Star,
    color: "from-yellow-500/20 via-yellow-400/10 to-transparent",
    bullets: [
      "Standard branding",
      "02 complimentary registrations",
      "Shared exhibition space",
      "Website logo visibility",
    ],
  },
  {
    title: "Silver",
    price: "₹1,00,000",
    icon: Medal,
    color: "from-gray-400/20 via-gray-300/10 to-transparent",
    bullets: [
      "Tailored branding benefits",
      "01 complimentary registration",
      "Product display / collaboration",
      "Opening/Closing recognition",
    ],
  },
]

/* -----------------------------------------------------------------------------
   SponsorsSection
----------------------------------------------------------------------------- */

export function SponsorsSection() {
  return (
    <section className="space-y-12 py-12 bg-gradient-to-b from-background via-muted/20 to-background">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="text-center"
      >
        <h2 className="lg:text-4xl text-3xl font-extrabold text-black">
          <p className="text-blue-500 inline-block">Sponsorship</p> Opportunities
        </h2>
        <p className="mt-4 text-muted-foreground max-w-2xl mx-auto text-base leading-relaxed">
          Showcase your brand to a focused audience in{" "}
          <strong>Biomaterials</strong>, <strong>Orthopaedics</strong>,{" "}
          <strong>AI in Healthcare</strong>, <strong>Ayurveda</strong> and more.
          <br />
          <span className="text-primary font-medium">
            Exhibition booths available (₹50,000)
          </span>{" "}
          for sponsors & non-sponsors.
        </p>
      </motion.div>

      {/* Tier Cards */}
      <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
        {tiers.map((tier, index) => (
          <motion.div
            key={tier.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.05, rotate: -0.5 }}
            transition={{ delay: index * 0.1, duration: 0.6 }}
            viewport={{ once: true }}
          >
            <TierCard {...tier} />
          </motion.div>
        ))}
      </div>

      {/* Benefits Section */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="relative rounded-2xl border bg-gradient-to-br from-background/80 via-muted/40 to-background/60 backdrop-blur-xl p-8 shadow-lg"
      >
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow">
          Why Sponsor Us?
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-4 text-center">
          <Benefit
            icon={Building}
            title="Brand Visibility"
            text="Boost your recognition among industry professionals and academic leaders."
          />
          <Benefit
            icon={Users}
            title="Networking Access"
            text="Connect with innovators, researchers, and investors shaping healthcare’s future."
          />
          <Benefit
            icon={HeartHandshake}
            title="CSR Impact"
            text="Contribute to education, research, and sustainable healthcare innovation."
          />
        </div>
      </motion.div>

      {/* Placeholder for Logos */}
      {/* <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <PhotoPlaceholder label="Sponsor Logos & Booth Photos (Coming Soon)" />
      </motion.div> */}
    </section>
  )
}

/* -----------------------------------------------------------------------------
   Tier Card
----------------------------------------------------------------------------- */

function TierCard({ title, price, bullets, icon: Icon, color }) {
  return (
    <div
      className={`rounded-2xl border bg-gradient-to-br ${color} p-6 shadow-sm hover:shadow-md transition-all duration-500 backdrop-blur-md`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-white/70 shadow-sm">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <h3 className="font-semibold text-base">{title} Sponsor</h3>
        </div>
        <span className="text-sm font-medium text-muted-foreground">{price}</span>
      </div>
      <ul className="text-sm text-muted-foreground space-y-1">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-primary mt-0.5" /> {b}
          </li>
        ))}
      </ul>
    </div>
  )
}

/* -----------------------------------------------------------------------------
   Benefit Card
----------------------------------------------------------------------------- */

function Benefit({ icon: Icon, title, text }) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="p-3 rounded-full bg-primary/10 mb-3">
        <Icon className="w-6 h-6 text-primary" />
      </div>
      <h4 className="font-semibold">{title}</h4>
      <p className="text-sm text-muted-foreground mt-1">{text}</p>
    </div>
  )
}
